import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BatchDetail } from '@/components/batches/batch-detail';
import { useReturn, useReturnTicket } from '@/lib/return-ticket';

export const Route = createFileRoute('/batches/$batchId/')({
  component: BatchDetailPage,
});

function BatchDetailPage() {
  const { batchId } = Route.useParams();
  const navigate = useNavigate();
  // "Batches" goes back to the log this batch was opened from, keeping the
  // search, the status chips and the scroll the brewer left there; a batch
  // reached from a link, a bookmark or the calendar has no such entry and
  // pushes `/batches` as it always did (PLAN-LIST-STATE D-LS7).
  const toBatches = useReturn(() => {
    void navigate({ to: '/batches' });
  });
  // Brew day is a step deeper, so it returns to this entry — which is still
  // holding the log's own ticket (D-LS8). A blend this batch went into is a
  // step deeper in the same way, so it takes the same ticket (D-BL5), and so
  // does a journal about it (D-J13).
  const ticket = useReturnTicket();
  return (
    // Keyed on the id because the router reuses this component when only the
    // param changes, and a reused page keeps everything it was holding. Each
    // batch is a new mount. (The workspace cache used to need this too; that
    // half is fixed in `store/workspace-context.tsx`.)
    <BatchDetail
      key={batchId}
      batchId={batchId}
      onBack={toBatches}
      onBrewDay={() => {
        void navigate({ to: '/batches/$batchId/brew', params: { batchId }, state: ticket });
      }}
      onCalendar={() => {
        void navigate({ to: '/calendar' });
      }}
      onFeedback={() => {
        void navigate({ to: '/batches/$batchId/feedback', params: { batchId } });
      }}
      onOpenBlend={(blendId) => {
        void navigate({ to: '/blending/$blendId', params: { blendId }, state: ticket });
      }}
      onOpenJournal={(journalId) => {
        void navigate({ to: '/journals/$journalId', params: { journalId }, state: ticket });
      }}
      // A fresh sheet with this beer on the chip row and nothing tapped yet
      // (PLAN-LABELS G4, D-LB9): the page is where the sheet is tapped out, so
      // there is nothing to hand it but the beer it was opened from. No recipe
      // opt-in either — it is the tasting cards' flag and the checkbox that
      // changes it is on that page, so a sheet always opens with the box as it
      // found it, off. The same ticket as brew day, so "Batches" from there
      // reaches the log this beer was opened from rather than a bare list.
      onPrintLabels={() => {
        void navigate({ to: '/batches/labels', search: { beers: batchId }, state: ticket });
      }}
      // The brew sheet for this batch's frozen copy of the recipe
      // (PLAN-UX-PASS C9). The same ticket as brew day and the labels: the
      // sheet is a step deeper, so its "Batch" returns to this entry and the
      // press after that reaches the log the beer was opened from.
      onPrintSheet={() => {
        void navigate({ to: '/batches/$batchId/sheet', params: { batchId }, state: ticket });
      }}
    />
  );
}
